/**
 * QuoteFormComponents.jsx — 견적서 폼 전용 하위 컴포넌트
 *
 * 홍보물 자동완성 입력, 무상제공품목 편집기, 브라우저 인쇄 포털,
 * 모바일 감지 훅을 한 파일에 묶어 QuoteManagement.jsx에서 import합니다.
 *
 * Exports:
 *   PromotionalAutocomplete — 홍보물 이름 자동완성 인풋 컴포넌트
 *   FreeItemsEditor         — 무상제공품목 목록 편집 컴포넌트
 *   PrintPortal             — ReactDOM.createPortal 인쇄 래퍼
 *   useIsMobile             — 768px 이하 모바일 감지 훅
 */
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import QuotePrintView from './QuotePrintView';
import { inputStyle, btnSecondary } from './constants';

// ── 홍보물 자동완성 인풋 ──────────────────────────────────────────────────────

/**
 * 홍보물 이름 자동완성 인풋 컴포넌트.
 * items 목록에서 query와 일치하는 항목을 최대 10개 드롭다운으로 보여줍니다.
 *
 * @param {object[]}  items    — [{id, name}] 홍보물 전체 목록
 * @param {string}    value    — 현재 선택/입력된 값
 * @param {function}  onChange — 값 변경 콜백 (name 문자열 전달)
 */
export function PromotionalAutocomplete({ items, value, onChange }) {
  const [query, setQuery]     = useState(value || '');
  const [open, setOpen]       = useState(false);
  const [focused, setFocused] = useState(false);

  // 부모 value prop이 바뀌면 로컬 query와 동기화
  useEffect(() => { setQuery(value || ''); }, [value]);

  // query 포함 여부로 필터링, 빈 쿼리면 전체 표시 (최대 10개)
  const filtered = (query.trim()
    ? items.filter((item) => item.name.toLowerCase().includes(query.toLowerCase()))
    : items
  ).slice(0, 10);

  function select(name) {
    setQuery(name);
    onChange(name);
    setOpen(false);
  }

  return (
    <div style={{ position: 'relative', flex: 2, minWidth: 0 }}>
      <input
        style={{
          ...inputStyle,
          borderColor: focused ? '#2563eb' : 'var(--border)',
          boxShadow: focused ? '0 0 0 2px rgba(37,99,235,0.12)' : 'none',
        }}
        value={query}
        placeholder="홍보물 검색..."
        onChange={(e) => { setQuery(e.target.value); onChange(e.target.value); setOpen(true); }}
        onFocus={() => { setFocused(true); setOpen(true); }}
        onBlur={() => { setFocused(false); setTimeout(() => setOpen(false), 120); }}
      />
      {/* 자동완성 드롭다운 */}
      {open && filtered.length > 0 && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 2px)', left: 0, right: 0,
          zIndex: 300, background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 8, boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
          maxHeight: 200, overflowY: 'auto',
        }}>
          {filtered.map((item) => (
            <div
              key={item.id}
              onMouseDown={() => select(item.name)}
              style={{ padding: '9px 14px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid var(--bg-subtle)' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#f0f7ff'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}
            >
              {item.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── 무상제공품목 편집기 ────────────────────────────────────────────────────────

/**
 * 무상제공품목 목록 편집 컴포넌트.
 * rows: [{name, qty}] 배열을 편집하며, 항목 추가/삭제와 수량 포맷(콤마) 처리합니다.
 *
 * @param {object[]}  promotionalItems — 홍보물 자동완성용 전체 목록
 * @param {object[]}  rows             — 현재 편집 중인 무상제공품목 배열
 * @param {function}  onChange         — rows 변경 콜백
 */
export function FreeItemsEditor({ promotionalItems, rows, onChange }) {
  // idx번째 행의 key 값을 val로 업데이트
  const upd = (idx, key, val) => onChange(rows.map((r, i) => i === idx ? { ...r, [key]: val } : r));

  // 수량 3자리 콤마 포맷 (예: 1000 → 1,000)
  function fmtQty(v) {
    const d = String(v || '').replace(/\D/g, '');
    return d ? Number(d).toLocaleString('ko-KR') : '';
  }

  return (
    <div>
      {rows.map((row, idx) => (
        <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
          {/* 홍보물 이름 자동완성 */}
          <PromotionalAutocomplete
            items={promotionalItems}
            value={row.name}
            onChange={(val) => upd(idx, 'name', val)}
          />
          {/* 수량 입력 */}
          <input
            style={{ ...inputStyle, width: 80, flexShrink: 0 }}
            value={row.qty}
            onChange={(e) => upd(idx, 'qty', fmtQty(e.target.value))}
            placeholder="수량"
          />
          {/* 항목 삭제 버튼 */}
          <button
            type="button"
            onClick={() => onChange(rows.filter((_, i) => i !== idx))}
            style={{
              background: 'none', border: '1px solid var(--border)', borderRadius: 6,
              color: 'var(--text-muted)', fontSize: 16, cursor: 'pointer',
              width: 34, height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            ×
          </button>
        </div>
      ))}
      {/* 항목 추가 버튼 */}
      <button
        type="button"
        onClick={() => onChange([...rows, { name: '', qty: '1' }])}
        style={{ ...btnSecondary, fontSize: 13, padding: '6px 14px', borderStyle: 'dashed', color: 'var(--text-muted)' }}
      >
        + 홍보물 추가
      </button>
    </div>
  );
}

// ── 브라우저 인쇄 포털 ────────────────────────────────────────────────────────

/**
 * 브라우저 인쇄(@media print) 전용 포털.
 * document.body에 직접 마운트하여 @media print 에서만 보이도록 CSS로 제어됩니다.
 * quote가 null이면 렌더링을 생략합니다.
 *
 * @param {object|null} quote — 인쇄할 견적서 데이터
 * @param {string|null} stamp — 도장 base64 URL
 * @param {string|null} logo  — 로고 base64 URL
 */
export function PrintPortal({ quote, stamp, logo }) {
  if (!quote) return null;
  return createPortal(
    <div id="quote-print-portal" style={{ width: '100%', boxSizing: 'border-box' }}>
      <QuotePrintView quote={quote} stamp={stamp} logo={logo} />
    </div>,
    document.body,
  );
}

// ── 모바일 감지 훅 ────────────────────────────────────────────────────────────

/**
 * 화면 너비 768px 이하이면 true를 반환하는 커스텀 훅.
 * resize 이벤트에 반응하여 동적으로 업데이트됩니다.
 */
export function useIsMobile() {
  const [mobile, setMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return mobile;
}
